import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  TransactionFormRule,
  TransactionFormSchema,
  VoidTransactionFormRule,
  VoidTransactionFormSchema,
} from "@factory/form/form.rule";
import { useFormDefaultTransaction } from "@factory/form/form-default-transaction.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseTransactionModel } from "@factory/model/query-model-transaction";
import {
  useQyDeleteTransaction,
  useQyGetTransactionById,
  useQyUpdateTransaction,
  useQyVoidTransaction,
} from "@factory/query/transaction.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { confirmDialog } from "primereact/confirmdialog";
import { useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditTransaction() {
  // LOCAL DECLARATION
  const { getDefault } = useFormDefaultTransaction();
  const navigate = useNavigate();
  const { mapUpdateTransaction, mapDeleteTransaction, mapVoidTransaction } =
    useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const [showVoidDialog, setShowVoidDialog] = useState<boolean>(false);
  const { transactionId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Void Transaction", type: "more", value: ActionEnum.Void },
  ];

  // QUERY DECLARATION
  // API GETBYID Transaction
  const handleGetApiSuccess = (data: GetResponseTransactionModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("tenant", responseData?.tenant || "");
    setValue("branch", responseData?.branch || "");
    setValue("userCode", responseData?.user_code || "");
    setValue("cardSeries", responseData?.card_series || "");
    setValue("reward", responseData?.reward || "");
    setValue("campaign", responseData?.campaign || "");
    setValue("action", responseData?.action || "");
    setValue("amount", responseData?.amount);
    setValue("points", responseData?.points);
    setValue("stamp", responseData?.stamp);
    setValue("remarks", responseData?.remarks || "");
    setValue("branchName", responseData?.branch_name || "");
    setValue(
      "customerName",
      `${responseData?.first_name} ${responseData?.last_name}`,
    );
    setValue("campaignName", responseData?.campaign_name || "");
    setValue("cardAlias", responseData?.card_alias || "");
    setValue("rewardEventName", responseData?.reward_name || "");
    setValue("voidedBy", responseData?.voided_by || "");
    setValue("voidedAt", responseData?.voided_at || "");
    setValue("voidedFirstName", responseData?.voided_first_name || "");
    setValue("voidedLastName", responseData?.voided_last_name || "");
    setValue("receiptNumber", responseData?.receipt_number || "");
  };
  const {
    data: transactionResponse,
    isLoading,
    isError: transactionError,
  } = useQyGetTransactionById(transactionId || "", handleGetApiSuccess);
  const transactionData = transactionResponse?.data?.[0];

  // API UPDATE Transaction
  const handleUpdateApiSuccess = () => {
    showSuccess("Transaction updated successfully");
  };
  const { mutate: updateTransaction, isLoading: isEditLoading } =
    useQyUpdateTransaction(handleUpdateApiSuccess);

  // API DELETE Transaction
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Transaction deleted successfully");
  };
  const { mutate: deleteTransaction } = useQyDeleteTransaction(
    handleDeleteApiSuccess,
  );

  // API VOID Transaction
  const handleVoidApiSuccess = () => {
    showSuccess("Transaction voided successfully");
    setShowVoidDialog(false);
  };
  const { mutate: voidTransaction } =
    useQyVoidTransaction(handleVoidApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<TransactionFormSchema>({
    defaultValues: getDefault(transactionData),
    resolver: zodResolver(TransactionFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: TransactionFormSchema) => {
    if (!transactionData || !transactionData.code) {
      throw new Error("No transaction data");
    }

    const formData = mapUpdateTransaction(form, transactionData.code);
    updateTransaction(formData);
  };
  const handleValidateError = (err: FieldErrors<TransactionFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  const voidFormMethod = useForm<VoidTransactionFormSchema>({
    defaultValues: {
      code: transactionId,
      voidedBy: "",
      authentication: "",
    },
    resolver: zodResolver(VoidTransactionFormRule),
  });
  const { handleSubmit: handleSubmitVoid } = voidFormMethod;
  const handleValidateVoid = (form: VoidTransactionFormSchema) => {
    if (!transactionData || !transactionData.code) {
      throw new Error("No transaction data");
    }
    const formData = mapVoidTransaction(form, transactionData.code);
    voidTransaction(formData);
  };
  const handleValidateVoidError = (
    err: FieldErrors<VoidTransactionFormSchema>,
  ) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleVoidTransaction = () => {
    handleSubmitVoid(handleValidateVoid, handleValidateVoidError)();
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!transactionData || !transactionData.code) {
      throw new Error("No transaction data found");
    }

    const form = mapDeleteTransaction(transactionData.code);
    deleteTransaction(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;

      case ActionEnum.Void:
        setShowVoidDialog(true);
        break;
    }
  };

  return {
    transactionId,
    transactionData,
    isLoading,
    transactionError,
    formMethod,
    actions,
    isEditLoading,
    voidFormMethod,
    showVoidDialog,
    setShowVoidDialog,
    handleVoidTransaction,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
